import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Box,
  List,
  ListItem,
  IconButton,
  Typography,
  DialogActions,
  ListItemText,
  Tooltip
} from '@mui/material';
import { downloadFileFromSignedUrl } from '../../../lib/files/file';
import { File } from '@esg/esg-global-types';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete Outlined.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/Download.svg';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { removeMetricRecordFile } from '../../../graphql/services/metric_record.service';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { GCP_FILE_UPLOAD_BUCKET } from '../../../env';
import { QuantitativeMetricRecordGridRow } from '../quantitative/QuantitativeMetricRecordGrid';
import { QualitativeMetricRecordGridRow } from '../qualitative/QualitativeMetricRecordGrid';

interface ManageSupportingDocsModalProps {
  open: boolean;
  metric_record: QuantitativeMetricRecordGridRow | QualitativeMetricRecordGridRow | null;
  metric_record_title: string;
  supporting_docs: Array<File>;
  view_only: boolean;
  handleCloseDocsModal: () => void;
  handleRemoveSupportingDoc: (metric_record_id: string, doc_id: string) => void;
}

/**
 * A modal allowing the user to view and delete supporting documents uploaded for a Metric Record.
 * @param {boolean} open The display state of the modal.
 * @param {QualitativeMetricRecordGridRow | QuantitativeMetricRecordGridRow | null} metric_record The metric record to manage docs for.
 * @param {string} metric_record_title The display name of the metric record.
 * @param {Array<File>} supporting_docs List of supporting documents belonging to given metric record.
 * @param {boolean} view_only Constrain functionality to view only actions.
 * @param {void} handleCloseDocsModal Handler to close the modal.
 * @param {void} handleRemoveSupportingDoc Handler function to remove a supporting document from a metric record row in parent grid.
 * @returns {JSX.Element}
 */
const ManageSupportingDocsModal = ({
  open,
  metric_record,
  metric_record_title,
  view_only,
  supporting_docs,
  handleCloseDocsModal,
  handleRemoveSupportingDoc
}: ManageSupportingDocsModalProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [delete_file, setDeleteFile] = React.useState<File | null>(null);
  const [deleteLoading, setDeleteLoading] = React.useState<boolean>(false);

  const handleCloseModal = (): void => {
    handleCloseDocsModal && handleCloseDocsModal();
  };

  const deleteSupportingDoc = async (delete_file: File): Promise<void> => {
    setDeleteLoading(true);
    try {
      if (group && company && metric_record) {
        await removeMetricRecordFile(group.id, company.id, metric_record.id, delete_file);
        handleRemoveSupportingDoc(metric_record.id, delete_file.id);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ManageSupportingDocs failed on an unknown error while calling removeFile.',
          {
            group: group,
            company: company,
            metric_record: metric_record,
            delete_file: delete_file
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to remove supporting document. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const downloadSupportingDocHandler = async (supporting_doc: File): Promise<void> => {
    try {
      await downloadFileFromSignedUrl(atob(GCP_FILE_UPLOAD_BUCKET), supporting_doc.file_path);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ManageSupportingDocs failed on an unknown error while calling downloadSupportDocHandler.',
          {
            supporting_doc: supporting_doc
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to download supporting document. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseModal}>
      <DialogTitle>Manage Supporting Documents</DialogTitle>
      {delete_file && (
        <DialogContent>
          <DialogContentText sx={{ marginY: 2 }}>
            Are you sure you want to Delete <b>{delete_file.name}</b>?
          </DialogContentText>

          <DialogActions sx={{ mt: 2 }}>
            <GhostButton dark onClick={() => setDeleteFile(null)}>
              Cancel
            </GhostButton>
            <PrimaryButton
              startIcon={<DeleteIcon />}
              loading={deleteLoading}
              onClick={async () => {
                await deleteSupportingDoc(delete_file);
                setDeleteFile(null);
              }}
              palette_color="error"
            >
              Delete
            </PrimaryButton>
          </DialogActions>
        </DialogContent>
      )}
      {!delete_file && (
        <DialogContent>
          <DialogContentText>{metric_record_title}</DialogContentText>
          <Box
            sx={{
              marginY: 6,
              border: 1,
              borderColor: 'rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
              paddingY: 3,
              paddingX: 1,
              textAlign: 'center'
            }}
          >
            {supporting_docs.length > 0 ? (
              <List dense={true}>
                {supporting_docs.map((doc: File) => {
                  return (
                    <ListItem key={doc.id}>
                      <Tooltip title={doc.name}>
                        <ListItemText
                          primary={
                            doc.name && doc.name.length > 25
                              ? doc.name.substring(0, 25) + '...'
                              : doc.name
                          }
                        />
                      </Tooltip>

                      <Tooltip title="Download File">
                        <IconButton
                          size="medium"
                          edge="end"
                          aria-label="delete"
                          color="primary"
                          sx={{ ml: 'auto' }}
                          onClick={() => {
                            downloadSupportingDocHandler(doc);
                          }}
                        >
                          <DownloadIcon height={25} stroke="currentColor" strokeWidth={2} />
                        </IconButton>
                      </Tooltip>
                      {!view_only && (
                        <Tooltip title="Delete File">
                          <IconButton
                            size="medium"
                            edge="end"
                            aria-label="delete"
                            color="primary"
                            sx={{ ml: 1 }}
                            onClick={() => {
                              setDeleteFile(doc);
                            }}
                          >
                            <DeleteIcon height={25} stroke="currentColor" strokeWidth={2} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography fontSize={'0.9rem'}>No Supporting Docs for Metric Record</Typography>
            )}
          </Box>
          <DialogActions sx={{ mt: 2 }}>
            <GhostButton dark onClick={handleCloseModal}>
              Cancel
            </GhostButton>
          </DialogActions>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ManageSupportingDocsModal;
